import axios, { AxiosError, AxiosResponse } from "axios";

// Extend Axios config to support a silent flag that suppresses error toasts
declare module "axios" {
  interface AxiosRequestConfig {
    silent?: boolean;
  }
  interface InternalAxiosRequestConfig {
    silent?: boolean;
  }
}
import { showToast } from "../pages/component/common/toastNotification";
import i18n from "../i18n";

const API_URL = process.env.REACT_APP_API_URL;
// const API_URL = "http://localhost:5503/api/";

export const httpTokenCommon = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

export const httpTokenFileUpload = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "multipart/form-data",
  },
});
export const httpTokenFileDownload = axios.create({
  baseURL: API_URL,
  responseType: "blob",
});

// Request Interceptor for Adding Token
httpTokenCommon.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
httpTokenFileUpload.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
httpTokenFileDownload.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
// Centralized Error Handling Function
const handleApiError = (error: AxiosError) => {
  // Silent requests suppress all error toasts (used for best-effort lookups)
  if (error.config?.silent) {
    return Promise.reject(error);
  }
  if (error.response) {
    const { status, data } = error.response as AxiosResponse<{
      message?: string;
      error?: string;
      status?: string;
    }>;

    const errorMessage =
      typeof data === "string" && data.trim()
        ? data.trim()
        : typeof data?.status === "string" && data.status.trim()
          ? data.status.trim()
          : data?.message || data?.error || i18n.t("errors.unexpectedError");

    switch (status) {
      case 400:
        showToast(errorMessage, "error");
        break;
      case 401:
        // Intentional logout or already cleared session — don't treat as expiry
        if (
          localStorage.getItem("logging_out") ||
          !localStorage.getItem("token")
        ) {
          break;
        }
        if (sessionStorage.getItem("ahcp.checkout.paymentInFlight")) {
          break;
        }
        if (!localStorage.getItem("session_expired_flag")) {
          showToast(i18n.t("errors.sessionExpired"), "error");
          localStorage.setItem("session_expired_flag", "true");

          setTimeout(() => {
            // If a fresh login happened while this timer was pending, abort the logout
            const loginTs = parseInt(localStorage.getItem("login_timestamp") || "0");
            if (Date.now() - loginTs < 4000) {
              localStorage.removeItem("session_expired_flag");
              return;
            }
            localStorage.clear();
            window.location.href = "/";
          }, 2000);
        }
        break;

      case 403:
        showToast(errorMessage, "error");
        break;
      case 404:
        showToast(errorMessage, "error");
        break;
      case 500:
        showToast(i18n.t("errors.serverUnavailable"), "error");
        break;
      default:
        showToast(errorMessage, "error");
    }
  } else {
    showToast(i18n.t("errors.networkError"), "error");
  }
  return Promise.reject(error);
};

// Response Interceptor for Error Handling
httpTokenCommon.interceptors.response.use(
  (response) => response,
  handleApiError
);

httpTokenFileUpload.interceptors.response.use(
  (response) => response,
  handleApiError
);
